import React, { useState, useRef } from 'react';
import { apiService } from '../services/apiService';

const OcrUploadComponent = ({ onOcrResult, onCancel }) => {
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            // Validate file type.
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/bmp', 'image/tiff'];
            if (!allowedTypes.includes(selectedFile.type.toLowerCase())) {
                setError('対応していないファイル形式です。JPG、PNG、BMP、TIFF の画像をアップロードしてください。');
                return;
            }

            // Validate file size (max 10MB).
            if (selectedFile.size > 10 * 1024 * 1024) {
                setError('ファイルサイズは10MB以下にしてください。');
                return;
            }

            setFile(selectedFile);
            setError('');
            
            // Generate preview.
            const reader = new FileReader();
            reader.onload = (e) => setPreviewUrl(e.target.result);
            reader.readAsDataURL(selectedFile);
        }
    };

    const handleOcrRecognition = async () => {
        if (!file) {
            setError('先にファイルを選択してください。');
            return;
        }

        setIsUploading(true);
        setError('');

        try {
            const result = await apiService.recognizeResidencePermit(file);
            
            if (result.success) {
                onOcrResult(result);
            } else {
                setError(result.message || 'OCRの読み取りに失敗しました。');
            }
        } catch (err) {
            setError('OCRの読み取り中にエラーが発生しました: ' + err.message);
        } finally {
            setIsUploading(false);
        }
    };

    const clearFile = () => {
        setFile(null);
        setPreviewUrl(null);
        setError('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
                📷 OCRで在留情報を読み取り
            </h3>
            
            <div className="space-y-4">
                {/* ファイル選択エリア */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="ocr-file-input"
                    />
                    
                    {!file ? (
                        <div>
                            <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <p className="mt-2 text-sm text-gray-600">
                                在留カード画像を選択、またはここにドラッグしてください
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                                JPG、PNG、BMP、TIFF に対応、最大10MB
                            </p>
                            <label
                                htmlFor="ocr-file-input"
                                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 cursor-pointer"
                            >
                                画像を選択
                            </label>
                        </div>
                    ) : (
                        <div>
                            <p className="text-sm text-gray-600 mb-2">選択中のファイル: {file.name}</p>
                            <p className="text-xs text-gray-500 mb-4">サイズ: {(file.size / 1024 / 1024).toFixed(2)} MB</p>
                            
                            {previewUrl && (
                                <div className="mb-4">
                                    <img
                                        src={previewUrl}
                                        alt="プレビュー"
                                        className="mx-auto max-h-48 max-w-full object-contain border rounded"
                                    />
                                </div>
                            )}
                            
                            <div className="flex justify-center space-x-2">
                                <button
                                    onClick={clearFile}
                                    className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                                >
                                    選択し直す
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* エラー表示 */}
                {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                )}

                {/* 操作ボタン */}
                <div className="flex justify-end space-x-3">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        disabled={isUploading}
                    >
                        キャンセル
                    </button>
                    <button
                        onClick={handleOcrRecognition}
                        disabled={!file || isUploading}
                        className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed"
                    >
                        {isUploading ? (
                            <div className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                読み取り中...
                            </div>
                        ) : (
                            '🔍 読み取り開始'
                        )}
                    </button>
                </div>

                {/* ヒント */}
                <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
                    <p><strong>💡 利用ヒント:</strong></p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                        <li>文字が読める鮮明な画像を使用してください</li>
                        <li>影、反射、ぼやけを避けてください</li>
                        <li>OCR読み取り後は内容を必ず確認してください</li>
                        <li>日本語、中国語、英語の読み取りに対応しています</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default OcrUploadComponent;
